import React, { useRef, useState } from 'react'
import Lightbox from 'react-image-lightbox'
import styled from 'styled-components'
import { A11y, Navigation } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css/bundle'
import { Image } from 'components/Image'
import { SwiperGalleryArrows, SwiperGalleryBox } from 'components/SwiperGallery'
import { useLightbox } from 'hooks'
import { IMAGE_BASE_WIDTH } from 'Theme'
import { useMedia } from 'useMedia'

const StyledImg = styled(Image)`
  cursor: pointer;
`

const StyledSwiper = styled(Swiper).withConfig({
  shouldForwardProp: (prop) => !['observerUpdate'].includes(prop),
})`
  width: 100%;
  height: 100%;
`

interface ExpertCarouselProps
  extends React.ComponentProps<typeof SwiperGalleryBox> {
  images: {
    id?: number
    url?: string
    alt?: string
  }[]
}

export const ExpertCarousel: React.FC<ExpertCarouselProps> = ({
  images,
  ...rest
}) => {
  const nextButtonRef = useRef(null)
  const prevButtonRef = useRef(null)
  const [isEnd, setIsEnd] = useState(false)
  const [isBeginning, setIsBeginning] = useState(true)
  const [lightboxActiveIndex, setLightboxActiveIndex] = useState(0)
  const { isLightboxOpen, openLightbox, closeLightbox } = useLightbox()
  const media = useMedia()

  return (
    <SwiperGalleryBox
      position="relative"
      width={{ MOBILE: '100%', TABLET: '95%' }}
      {...rest}
    >
      <StyledSwiper
        isEnd={isEnd}
        isBeginning={isBeginning}
        modules={[A11y, Navigation]}
        slidesPerView={media.MOBILE ? 1 : 1.5}
        spaceBetween={1}
        normalizeSlideIndex={false}
        observerUpdate
        observer
        watchOverflow
        onSlideChange={(currentSwiperInstance) => {
          setIsBeginning(currentSwiperInstance.isBeginning)
          setIsEnd(currentSwiperInstance.isEnd)
        }}
        navigation={{
          prevEl: prevButtonRef.current,
          nextEl: nextButtonRef.current,
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={image.id}>
            <StyledImg
              baseWidth={IMAGE_BASE_WIDTH.PX_500}
              alt={image.alt || "Expert's exhibition photo"}
              src={image.url}
              onClick={() => {
                setLightboxActiveIndex(index)
                openLightbox()
              }}
            />
          </SwiperSlide>
        ))}
      </StyledSwiper>
      <SwiperGalleryArrows
        prevButtonRef={prevButtonRef}
        nextButtonRef={nextButtonRef}
      />
      {isLightboxOpen && (
        <Lightbox
          mainSrc={images[lightboxActiveIndex]?.url}
          nextSrc={images[(lightboxActiveIndex + 1) % images.length]?.url}
          prevSrc={
            images[(lightboxActiveIndex + images.length - 1) % images.length]
              ?.url
          }
          onCloseRequest={closeLightbox}
          onMovePrevRequest={() => {
            setLightboxActiveIndex(
              (prevState) => (prevState + images.length - 1) % images.length
            )
          }}
          onMoveNextRequest={() => {
            setLightboxActiveIndex(
              (prevState) => (prevState + 1) % images.length
            )
          }}
        />
      )}
    </SwiperGalleryBox>
  )
}
